import classNames from 'classnames/bind';
import React, { forwardRef } from 'react';
import { createSearchParams, Link } from 'react-router-dom';
import { Tabs, Widget, DataView, SortableWidgetProps } from '@creditsights/styleguide-web';
import { COMPANY_ROUTES, ROUTES } from '../../../Base.constants';
import useCheckArticleAccessStatus from '../../../hooks/articles/useCheckArticleAccessStatus';
import { useCustomNavigate } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import { ContentTypeFilter } from '../../../models';
import DisplayTypeSelection, { DisplayType } from '../../../pages/Feeds/FeedLeftContainer/DisplayTypeSelection';
import NoResults from '../../NoResults';
import ResultsFilter from '../../ResultsFilter/ResultsFilter';
import { useFetchArticles } from '../../../hooks/articles';
import {
  getRecentlyPublishedTabs,
  viewConfig,
  getContentTypeTagFilter,
} from '../../../pages/Company/RecentlyPublished.logic';

const ARTICLE_COUNT_FILTER_VALUES = [5, 10, 25];
export const MIN_PAGINATION_ARTICLE_COUNT = 6;

const getKey = (article: ArticleTableRow) => article.id;

const HeaderLink = ({ children }) => <Link to={COMPANY_ROUTES.recentlyPublished}>{children}</Link>;

interface RecentlyPublishedWidgetProps extends SortableWidgetProps {
  issuerTag: string;
  articleTypeFilter: ContentTypeFilter;
  onArticleTypeFilterChange: (filter: ContentTypeFilter) => void;
  articleCount: number;
  onArticleCountChange: (filter: number) => void;
  displayType: DisplayType;
  setDisplayType: (displayType: DisplayType) => void;
  canAccess?: boolean;
  noPrint?: boolean;
}

const RecentlyPublishedWidget = (props: RecentlyPublishedWidgetProps, ref): React.ReactElement | null => {
  const {
    issuerTag,
    articleTypeFilter,
    onArticleTypeFilterChange,
    articleCount,
    onArticleCountChange,
    displayType,
    setDisplayType,
    noPrint,
    ...sortableWidgetProps
  } = props;

  const { navigateOnMouseUp } = useCustomNavigate();
  const { data: userPermissions } = useFetchUserPermissions();

  const {
    data: articles = { rows: [], totalResults: 0 },
    isLoading,
    isError,
    refetch,
  } = useFetchArticles({
    take: 25,
    page: 0,
    filters: {
      tags: getContentTypeTagFilter(articleTypeFilter),
      focusTagId: issuerTag,
    },
    retry: false,
  });
  const checkArticleStatus = useCheckArticleAccessStatus();

  const tabs = getRecentlyPublishedTabs(articleTypeFilter, onArticleTypeFilterChange, userPermissions!.permissions);

  const renderFooter = () => {
    if (articles.totalResults < MIN_PAGINATION_ARTICLE_COUNT) {
      return null;
    }

    return (
      <ResultsFilter
        selectedValue={articleCount}
        values={ARTICLE_COUNT_FILTER_VALUES}
        onResultsCountChange={onArticleCountChange}
        optionTestIdPrefix="article-count-option"
        allValueLink={{
          pathname: COMPANY_ROUTES.recentlyPublished,
          search: createSearchParams({
            type: articleTypeFilter,
          }).toString(),
        }}
      />
    );
  };

  const onArticleClick = (article: ArticleTableRow, event) => {
    checkArticleStatus(article.id, () => {
      navigateOnMouseUp(event, { route: ROUTES.article(article.id) });
    });
  };

  const config = viewConfig(onArticleClick, articleTypeFilter === ContentTypeFilter.ALL, userPermissions!);
  return (
    <div className={classNames({ 'no-print': articles.totalResults === 0 })}>
      <Widget
        classNames={classNames({ 'no-print': noPrint })}
        headerTitle="Recently Published"
        headerLink={HeaderLink}
        firstLine={<DisplayTypeSelection selected={displayType} onSelect={setDisplayType} />}
        secondLine={<Tabs tabs={tabs} dataTestId="recently-published-tabs" />}
        isLoading={isLoading}
        footer={renderFooter()}
        showFooterDivider
        fixedHeight
        dataTestId="recently-published-widget"
        hideFirstLineWhenLoading={false}
        hideSecondLineWhenLoading={false}
        ref={ref}
        isError={isError}
        onRetryAfterErrorClick={refetch}
        {...sortableWidgetProps}
      >
        {articles.totalResults === 0 ? (
          <NoResults />
        ) : (
          <DataView
            data={articles.rows.slice(0, articleCount)}
            config={config}
            getKey={getKey}
            expanded={displayType === DisplayType.Expanded}
          />
        )}
      </Widget>
    </div>
  );
};

export default forwardRef(RecentlyPublishedWidget);
